import React, { createContext, useContext } from "react"
import { FormProvider } from "react-hook-form"

import type { ProjectBudgetForm_project$data } from "./__generated__/ProjectBudgetForm_project.graphql"
import type { ProjectBudgetForm_user$data } from "./__generated__/ProjectBudgetForm_user.graphql"

import { BudgetRoleTypes } from "~/helpers/otherCosts"

import { RateTypeSelection } from "~/GLOBALVARS"

import { BudgetedRole } from "./types"
import { useProjectBudgetForm } from "./useProjectBudgetForm"

type Props = {
  project: ProjectBudgetForm_project$data
  user: ProjectBudgetForm_user$data
  children: React.ReactNode
}

type ProjectBudgetFormContextType = {
  totalAllocated: number
  totalExpenses: {
    charge: number
    cost: number
    margin: string
    profit: number
  }
  rateType: RateTypeSelection["value"]
  budgetAllocationDifference: number
  expensesAllocationDifference: number
  hasExpenses: boolean
  showNonBillableView: boolean
  showFinancialColumns: boolean
  showDayView: boolean
  hideFinancials: boolean
  hasFinancialPermissions: boolean
  getMutationInput: () => any
  extractedRoles: BudgetRoleTypes<BudgetedRole>
}

const ProjectBudgetFormContext =
  createContext<ProjectBudgetFormContextType | null>(null)

export const useProjectBudgetFormContext = () => {
  const context = useContext(ProjectBudgetFormContext)
  if (!context) {
    throw new Error(
      "useProjectBudgetFormContext must be used within ProjectBudgetFormProvider",
    )
  }
  return context
}

export const ProjectBudgetFormProvider = ({
  project,
  user,
  children,
}: Props) => {
  const formHook = useProjectBudgetForm({ project, user })

  const contextValue: ProjectBudgetFormContextType = {
    totalAllocated: formHook.totalAllocated,
    totalExpenses: formHook.totalExpenses,
    budgetAllocationDifference: formHook.budgetAllocationDifference,
    expensesAllocationDifference: formHook.expensesAllocationDifference,
    hasExpenses: formHook.hasExpenses,
    showNonBillableView: formHook.showNonBillableView,
    showDayView: formHook.showDayView,
    showFinancialColumns: formHook.showFinancialColumns,
    hideFinancials: formHook.hideFinancials,
    hasFinancialPermissions: formHook.hasFinancialPermissions,
    getMutationInput: formHook.getMutationInput,
    extractedRoles: formHook.extractedRoles,
    rateType: formHook.rateType,
  }

  return (
    <FormProvider {...formHook.formMethods}>
      <ProjectBudgetFormContext.Provider value={contextValue}>
        {children}
      </ProjectBudgetFormContext.Provider>
    </FormProvider>
  )
}

export { useProjectBudgetForm }
